import posthog from "posthog-js";
import useAuthStore from "@/store/useAuthStore";
import { isLearnTest } from "./utils";

// 防抖延迟时间（毫秒）
const DEBOUNCE_DELAY = 300;

// 存储每个事件的防抖定时器
const debounceTimers = new Map<string, NodeJS.Timeout>();
// 存储特定事件上一次触发时间（用于自动事件限流）
const lastEventTriggerAt = new Map<ReportEvent, number>();

// 防抖 key 中忽略的高波动字段，避免 timestamp 导致去重失效
const VOLATILE_DEBOUNCE_KEYS = new Set([
  'timestamp',
  'report_time',
  'currentTimeUTC',
  'currentTimeLocal',
]);

const normalizeDebounceData = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normalizeDebounceData);
  }

  if (value && typeof value === 'object') {
    const normalized: Record<string, unknown> = {};
    for (const [key, innerValue] of Object.entries(value as Record<string, unknown>)) {
      if (VOLATILE_DEBOUNCE_KEYS.has(key)) continue;
      normalized[key] = normalizeDebounceData(innerValue);
    }
    return normalized;
  }

  return value;
};

export enum ReportEvent {
  // 用户点击了landing页面的视频播放按钮
  USER_LANDING_VIDEO_PLAY_CLICK = 'user_landing_video_play_click',
  // chunk
  CHUNK_REFRESH = 'chunk_refresh',
  // 自动刷新
  AUTO_REFRESH = 'auto_refresh',
  // 手动刷新
  MANUAL_REFRESH = 'manual_refresh',
  // 学习引导的rect坐标
  LEARN_GUIDE_RECT = 'learn_guide_rect',
  // 家长引导的rect坐标
  QUEST_PARENT_GUIDE_RECT = 'quest_parent_guide_rect',
  // 冒险收集礼物失败
  ADVENTURE_COLLECT_GIFT_FAIL = 'adventure_collect_gift_fail',
  // 分享邀请码
  SHARE_INVITE_CODE = 'share_invite_code',
  // 分享邀请码的点击
  REFERRAL_LINK_CLICK = 'referral_link_click',
  // 进入了learn页面
  USER_ENTER_LEARN = 'user_enter_learn',
  // 点击了learn页面的continue按钮，用户主动操作说明在学习
  LEARN_CONTINUE = 'learn_click_continue',
  // 在learn页面点击了Ask Pengi按钮
  LEARN_ASK_PENGI_BUTTON = 'learn_ask_pengi_button',
  // ask pengi的内容，在learn页面实际地提问了
  LEARN_ASK_PENGI_TEXT = 'learn_ask_pengi_text',
  // 点击了learn页面的show explain按钮
  LEARN_SHOW_EXPLAIN = 'learn_show_explain',
  // 点击了learn页面的more details按钮
  LEARN_SHOW_MORE_DETAILS = 'learn_show_more_details',
  // 完成了本节课
  USER_FINISH_LEARN = 'user_finish_learn',
  // 用户完成了每日任务
  USER_COMPLETE_DAILY_TASK = 'user_complete_daily_task',
  // 完成了learn、practice、play页面的congratulation弹窗
  USER_COMPLETE_CONGRATULATION = 'user_complete_congratulation',
  // 点击了header的back按钮
  HEADER_BACK_BUTTON = 'header_back_button',
  // 点击了header的chapter title按钮
  HEADER_CHAPTER_TITLE_CLICK = 'header_chapter_title_click',
  // 用户手动触发音量按钮,传ture的时候表示用户打开音量,传false的时候表示用户关闭音量
  USER_MANUAL_VOLUME_BUTTON = 'user_manual_volume_button',
  // 用户点击的report按钮
  USER_REPORT_BUTTON = 'user_report_button',
  // 用户提交的report按钮
  USER_REPORT_BUTTON_COMPLETE = 'user_report_button_complete',
  // 用户点击了learn里的ChatRecommendShorts的短视频
  LEARN_OPEN_SHORTS = 'Learn_open_shorts',
  // 用户点击了learn里的ChatRecommendVideos的短视频
  LEARN_OPEN_VIDEO = 'Learn_open_video',
  // 用户点击了learn里的LessonSummaryCard的音频
  USER_LESSON_SUMMARY_AUDIO_CLICK = 'user_lesson_summary_audio_click',
  // 用户点击了图片放大查看
  LEARN_IMAGE_ZOOM_CLICK = 'Learn_image_zoom_click',
  // 用户点击了flashcard的shuffle按钮
  LEARN_FLASHCARD_SHUFFLE = 'learn_flashcard_shuffle',
  // 用户点击了flashcard的flip按钮
  LEARN_FLASHCARD_FLIP = 'learn_flashcard_flip',
  // 用户点击了flashcard的audio播放按钮
  LEARN_FLASHCARD_AUDIO_PLAY = 'learn_flashcard_audio_play',
  // 用户点击了learn里的ChatExampleQuestion的按钮，可选的又"init" | "skip" | "hint" | "question"
  LEARN_EXAMPLE_BUTTON_CLICK = 'Learn_example_button_click',
  // 用户点击了learn里的ChatKeyProperty的按钮，可选的又"init" | "easy" | "hint" | "got" | "memorize"
  LEARN_KEY_PROPERTY_BUTTON_CLICK = 'learn_key_property_button_click',
  // 用户点击了learn里的interactive
  LEARN_INTERACTIVE_CLICK = 'learn_interactive_click',
  // 用户点击了quest的tab
  USER_QUEST_TAB_CLICK = 'user_quest_tab_click',
  // 用户点击了会员按钮
  USER_MEMBERSHIP_CLICK = 'user_membership_click',
  // 用户点击了通知按钮，右上角按钮
  USER_NOTIFICATION_ICON_CLICK = 'user_notification_icon_click',
  // 用户点击了通知，主动选择并点击了一个通知查看
  USER_NOTIFICATION_OPEN = 'user_notification_open',
  // 强制向用户显示通知 
  USER_NOTIFICATION_SHOW_FORCE = 'user_notification_show_force',
  // 用户点击消息弹窗的关闭
  USER_NOTIFICATION_CLOSE = 'user_notification_close',
  // 用户点击了quest的avatar按钮
  USER_QUEST_AVATAR_CLICK = 'user_quest_avatar_click',
  // 用户点击了quest的avatar编辑按钮
  USER_QUEST_AVATAR_EDIT_CLICK = 'user_quest_avatar_edit_click',
  // 用户点击了quest的avatar编辑取消按钮
  USER_QUEST_AVATAR_EDIT_CANCEL_CLICK = 'user_quest_avatar_edit_cancel_click',
  // 用户点击了quest的avatar编辑保存按钮
  USER_QUEST_AVATAR_EDIT_SAVE_CLICK = 'user_quest_avatar_edit_save_click',
  // 用户点击了quest的avatar退出登录按钮
  USER_QUEST_AVATAR_SIGN_OUT_CLICK = 'user_quest_avatar_sign_out_click',
  // 用户点击了streak的icon
  USER_STREAK_ICON_CLICK = 'user_streak_icon_click',
  // 用户点击了diamond的icon
  USER_DIAMOND_ICON_CLICK = 'user_diamond_icon_click',
  // 用户点击了heart的icon
  USER_HEART_ICON_CLICK = 'user_heart_icon_click',
  // 用户点击了pet的function
  USER_PET_FUNCTION_OPEN = 'user_pet_function_open',
  // 用户点击了pet的card
  USER_PET_CARD_CLICK = 'user_pet_card_click',
  // 用户点击了pet的body
  USER_PET_BODY_CLICK = 'user_pet_body_click',
  // 用户点击了pet的home的send message按钮
  USER_PET_HOME_SEND_MESSAGE = 'user_pet_home_send_message',
  // 用户点击了schedule的add按钮
  USER_SCHEDULE_ADD_CLICK = 'user_schedule_add_click',
  // 用户点击了book的upload按钮
  USER_BOOK_UPLOAD_CLICK = 'user_book_upload_click',
  // 用户点击了book的view all按钮
  USER_BOOK_VIEW_ALL_CLICK = 'user_book_view_all_click',
  // 用户点击了check homework的按钮
  USER_ENTER_CHECK_HOMEWORK = 'user_enter_check_homework',
  // 用户选择的模式
  USER_SELECT_CHECK_HOMEWORK_MODE = 'user_select_check_homework_mode',
  // 用户退出了check homework页面
  USER_EXIT_CHECK_HOMEWORK = 'user_exit_check_homework',
  // 用户点击了textbook的problems and solutions按钮
  USER_TEXTBOOK_PROBLEMS_AND_SOLUTIONS_CLICK = 'user_textbook_problems_and_solutions_click',
  // 用户点击了prepare my test按钮
  USER_PREPARE_MY_TEST_CLICK = 'user_prepare_my_test_click',
  // 用户点击了my mistakes按钮
  USER_MY_MISTAKES_CLICK = 'user_my_mistakes_click',
  // 用户点击了feedback survey的expand按钮,open: true表示展开,open: false表示关闭
  USER_FEEDBACK_SURVEY_EXPANDED = 'user_feedback_survey_expanded',
  // 用户点击了dashboard里的按钮
  USER_DASHBOARD_CLICK = 'user_dashboard_click',
  // 用户点击了pet聊天的缩放
  USER_PET_CHAT_EXPANDED = 'user_pet_chat_expanded',
  // 切换了页面
  PATH_NAME_CHANGE = 'user_path_name_change',
  // 用户点击了具体的某一个课外视频
  USER_EXTRACURRICULAR_ITEM_CLICK = 'user_extracurricular_item_click',
  // 用户更改了课外视频的筛选器
  USER_EXTRACURRICULAR_FILTER = 'user_extracurricular_filter',
  // 用户在play页面开始玩游戏
  USER_PLAY_PAGE_CLICK = 'user_play_pagee_click',
  // 用户点击了game center里的按钮，主要是标记点击了哪个游戏
  USER_GAME_CENTER_CLICK = 'user_game_center_click',
  // 用户点击了game center里的add time按钮
  USER_GAME_CENTER_ADD_TIME_CLICK = 'user_game_center_add_time_click',
  // 用户进入了practice页面
  USER_ENTER_PRACTICE = 'user_enter_practice',
  // 用户完成了practice
  USER_FINISH_PRACTICE = 'user_finish_practice',
  // 用户进入了play页面
  USER_ENTER_PLAY = 'user_enter_play',
  // 用户主动下载了pet的card
  USER_PET_CARD_DOWNLOAD = 'user_pet_card_download',
  // 用户主动分享了pet的card
  USER_PET_CARD_SHARE = 'user_pet_card_share',
  // 用户在practice页面点击了ask pengi按钮
  PRACTICE_ASK_PENGI_BUTTON = 'practice_ask_pengi_button',
  // 用户打开了book的toc
  USER_BOOK_TOC_OPEN = 'User_book_toc_open',
  // 用户关闭了book的toc
  USER_BOOK_TOC_CLOSE = 'User_book_toc_close',
  // 用户点击了book的filter按钮
  USER_BOOK_FILTER = 'User_book_filter',
  // 用户点击了streak的claim按钮
  USER_STREAK_CLAIM = 'user_streak_claim',
  // 用户开始了冒险
  USER_ADVENTURE_START = 'user_adventure_start',
  // 用户完成了冒险
  USER_ADVENTURE_COMPLETE = 'user_adventure_complete',
  // 用户打开了冒险准备弹窗
  USER_OPEN_ADVENTURE_READY_MODAL = 'user_open_adventure_ready_modal',
  // 用户打开了冒险解锁弹窗
  USER_OPEN_ADVENTURE_UNLOCK_MODAL = 'user_open_adventure_unlock_modal',
  // 用户打开了旅行奖励卡片
  USER_OPEN_REWARD_CARD = 'user_open_reward_card',
  // 用户翻转post card
  USER_ADVENTURE_REWARD_FLIP = 'user_adventure_reward_flip',

  // ========== DrawAFish 画鱼页面事件 ==========
  // 页面进入和导航
  DRAWAFISH_PAGE_ENTER = 'drawafish_page_enter',
  DRAWAFISH_TAB_CLICK = 'drawafish_tab_click',

  // 画鱼流程（简化版）
  DRAWAFISH_DRAW_TAB_ENTER = 'drawafish_draw_tab_enter',
  DRAWAFISH_FISH_SUBMIT_SUCCESS = 'drawafish_fish_submit_success',

  // AI优化功能
  DRAWAFISH_AI_ENHANCE_CLICK = 'drawafish_ai_enhance_click',
  DRAWAFISH_AI_ENHANCE_CONFIRM = 'drawafish_ai_enhance_confirm',
  DRAWAFISH_AI_ENHANCE_SELECT = 'drawafish_ai_enhance_select',

  // 社交互动 - 投票
  DRAWAFISH_FISH_VOTE_CLICK = 'drawafish_fish_vote_click',
  DRAWAFISH_FISH_VOTE_SUCCESS = 'drawafish_fish_vote_success',
  DRAWAFISH_FISH_VOTE_FAIL = 'drawafish_fish_vote_fail',

  // 社交互动 - 分享
  DRAWAFISH_FISH_SHARE_CLICK = 'drawafish_fish_share_click',
  DRAWAFISH_SCHOOL_SHARE_CLICK = 'drawafish_school_share_click',

  // 社交互动 - 排行榜
  DRAWAFISH_RANKING_VIEW_ENTER = 'drawafish_ranking_view_enter',
  DRAWAFISH_RANKING_SORT_CHANGE = 'drawafish_ranking_sort_change',
  DRAWAFISH_RANKING_FISH_CLICK = 'drawafish_ranking_fish_click',

  // 坦克互动
  DRAWAFISH_TANK_VIEW_ENTER = 'drawafish_tank_view_enter',
  DRAWAFISH_TANK_INTERACTION_MODE = 'drawafish_tank_interaction_mode',
  DRAWAFISH_TANK_FOOD_SELECT = 'drawafish_tank_food_select',
  DRAWAFISH_TANK_FEED_FISH = 'drawafish_tank_feed_fish',
  DRAWAFISH_TANK_SCARE_FISH = 'drawafish_tank_scare_fish',
  DRAWAFISH_TANK_FISH_CLICK = 'drawafish_tank_fish_click',

  // 任务游戏
  DRAWAFISH_MISSION_OPEN = 'drawafish_mission_open',
  DRAWAFISH_MISSION_CLOSE = 'drawafish_mission_close',
  DRAWAFISH_MISSION_START = 'drawafish_mission_start',
  DRAWAFISH_MISSION_ANSWER_SELECT = 'drawafish_mission_answer_select',
  DRAWAFISH_MISSION_POWERUP_PURCHASE = 'drawafish_mission_powerup_purchase',
  DRAWAFISH_MISSION_LEVEL_COMPLETE = 'drawafish_mission_level_complete',
  DRAWAFISH_MISSION_GAME_OVER = 'drawafish_mission_game_over',

  // 用户点击了more book按钮
  USER_BOOK_VIEW_MORE_CLICK = 'user_book_view_more_click',

  // 用户点击了PengiSubjectCard书本进行跳转
  USER_PENGI_BOOK_CLICK = 'user_pengi_book_click',

  // 用户打开了review modal
  LEARN_REVIEW_MODAL_OPEN = 'learn_review_modal_open',
  LEARN_REVIEW_MODE_START = 'learn_review_modal_close',
  LEARN_REVIEW_MODE_END = 'learn_review_mode_end',

  PRACTICE_REVIEW_MODAL_OPEN = 'practice_review_modal_open',
  PRACTICE_REVIEW_MODE_START = 'practice_review_mode_start',

  // 用户点击了joystick按钮
  USER_CLICK_JOYSTICK = 'user_click_joystick',
  USER_PAY_FOR_RESPIN = 'user_pay_for_respin',
  USER_PAY_FOR_RESPIN_SUCCESS = 'user_pay_for_respin_success',

  USER_OPEN_BOOKSHELF = 'user_open_bookshelf',
  USER_ADD_BOOK_TO_BOOKSHELF = 'user_add_book_to_bookshelf',
  USER_REMOVE_BOOK_FROM_BOOKSHELF = 'user_remove_book_from_bookshelf',

  // christmas event埋点
  CHRISTMAS_EVENT_ENTER = 'christmas_event_enter',
  CHRISTMAS_EVENT_CANCEL = 'christmas_event_cancel',
  CHRISTMAS_EVENT_EXIT = 'christmas_event_exit',
  CHRISTMAS_EVENT_DRAG_START = 'christmas_event_drag_start',
  CHRISTMAS_EVENT_DRAG_END = 'christmas_event_drag_end',

  // magic lab埋点
  USER_MAGIC_LAB_ENTER = 'user_magic_lab_enter',
  MAGIC_LAB_IMAGE_GEN_START = 'magic_lab_image_gen_start',
  MAGIC_LAB_IMAGE_GEN_SUCCESS = 'magic_lab_image_gen_success',
  MAGIC_LAB_IMAGE_GEN_RETRY = 'magic_lab_image_gen_retry',
  MAGIC_LAB_IMAGE_GEN_FAIL = 'magic_lab_image_gen_fail',
  MAGIC_LAB_VIDEO_GEN_START = 'magic_lab_video_gen_start',
  MAGIC_LAB_VIDEO_GEN_SUCCESS = 'magic_lab_video_gen_success',
  MAGIC_LAB_VIDEO_GEN_RETRY = 'magic_lab_video_gen_retry',
  MAGIC_LAB_VIDEO_GEN_FAIL = 'magic_lab_video_gen_fail',
  USER_SAVE_MINIPET = 'user_save_minipet',

  // 错误处理相关事件
  ERROR_BOUNDARY_TRIGGERED = 'error_boundary_triggered',
  ERROR_LOGGED = 'error_logged',
  NODE_API_ERROR = 'node-api-error',
  MATH_EXPRESSION_MATCH_FAILED = 'math_expression_match_failed',

  MINIPET_SHARE_VOTE_CLICK = 'minipet_share_vote_click',
  MINIPET_SHARE_BUTTON_CLICK = 'minipet_share_button_click',

  // Pet Adventure Notifications
  PUSH_NOTIFICATION_CLICKED = 'push_notification_clicked', // 用户点击系统推送通知返回网站 (URL: ?from=push)
  TAB_NOTIFICATION_SHOWN = 'tab_notification_shown', // 标签页标题开始闪烁
  TAB_NOTIFICATION_CLICKED = 'tab_notification_clicked', // 用户返回标签页
  // Notification Permission Modal
  NOTIFICATION_PERMISSION_MODAL_SHOWN = 'notification_permission_modal_shown', // 弹窗显示 (包含 permissionState)
  NOTIFICATION_PERMISSION_MODAL_ENABLE_CLICKED = 'notification_permission_modal_enable_clicked', // 点击启用按钮 (包含操作结果)
  NOTIFICATION_PERMISSION_MODAL_DISMISSED = 'notification_permission_modal_dismissed', // 点击取消/关闭按钮 (包含 permissionState)
  NOTIFICATION_PERMISSION_STATUS = 'notification_permission_status', // 用户通知权限状态 (每次页面加载时上报)

  // Profile update from quick start
  USER_PROFILE_UPDATE_ENTER = 'user_profile_update_enter',


  // Pet Setting
  PET_SETTING_TAB_CLICK = 'pet_setting_tab_click',
  PET_SETTING_PERSONALITY_SHUFFLE = 'pet_setting_personality_shuffle',
  PET_SETTING_PERSONALITY_SAVED = 'pet_setting_personality_saved',
  PET_SETTING_ABOUT_YOU_SAVED = 'pet_setting_about_you_saved',

  // TTS Button Events
  TTS_BUTTON_CLICK = 'tts_button_click', // 用户点击了TTS播放按钮
  TTS_PLAYBACK_START = 'tts_playback_start', // TTS开始播放

  // ========== Feed 相关事件 ==========
  // Feed列表展示
  FEED_LIST_VIEW = 'feed_list_view', // FeedList组件加载展示
  FEED_LIST_LOAD_MORE = 'feed_list_load_more', // 加载更多Feed

  // Feed Item交互
  FEED_ITEM_CLICK = 'feed_item_click', // 点击FeedItem
  FEED_ITEM_LIKE = 'feed_item_like', // 点赞FeedItem
  FEED_ITEM_UNLIKE = 'feed_item_unlike', // 取消点赞FeedItem

  // ========== FlowQuiz Teacher Landing 相关事件 ==========
  FLOW_QUIZ_SIGN_UP_CLICK = 'flow_quiz_sign_up_click', // 点击Sign up按钮
  FLOW_QUIZ_SIGN_IN_CLICK = 'flow_quiz_sign_in_click', // 点击Sign in按钮
  FLOW_QUIZ_LOGIN_MODAL_OPEN = 'flow_quiz_login_modal_open', // 打开登录弹窗
  FLOW_QUIZ_LOGIN_MODAL_CLOSE = 'flow_quiz_login_modal_close', // 关闭登录弹窗
  FLOW_QUIZ_SIGN_UP_SUCCESS = 'flow_quiz_sign_up_success', // 注册成功
  FLOW_QUIZ_SIGN_IN_SUCCESS = 'flow_quiz_sign_in_success', // 登录成功
  FLOW_QUIZ_ONBOARD_COMPLETE = 'flow_quiz_onboard_complete', // 完成onboarding
  FLOW_QUIZ_PROFILE_UPDATE = 'flow_quiz_profile_update', // 更新个人信息
  FLOW_QUIZ_VIDEO_CLICK = 'flow_quiz_video_click', // 点击视频内容
  FLOW_QUIZ_FILTER_CLICK = 'flow_quiz_filter_click', // 点击标签筛选

  // Player相关事件
  FLOW_QUIZ_DISPLAY_MODE_CHANGE = 'flow_quiz_display_mode_change', // 切换显示模式(video/particles/lyrics)
  FLOW_QUIZ_SHARE_CLICK = 'flow_quiz_share_click', // 点击分享按钮
  FLOW_QUIZ_SHARE_MODAL_OPEN = 'flow_quiz_share_modal_open', // 打开分享弹窗
  FLOW_QUIZ_SHARE_MODAL_CLOSE = 'flow_quiz_share_modal_close', // 关闭分享弹窗
  FLOW_QUIZ_VIDEO_WATCH_COMPLETE = 'flow_quiz_video_watch_complete', // 用户完整观看视频(播放>=95%)
  FLOW_QUIZ_VIDEO_WATCH_INTERRUPTED = 'flow_quiz_video_watch_interrupted', // 用户中途中断观看

  // Chrome Extension 注册来源追踪
  EXTENSION_USER_REGISTERED = 'extension_user_registered',

  // 用户点击 "Add to Chrome" CTA,准备跳转 Chrome Web Store 安装扩展
  // 这是 PhET 广告 → 扩展安装 funnel 的关键桥梁,因为后续跳转离站后追踪会断
  EXTENSION_INSTALL_INITIATED = 'extension_install_initiated',
}

// 自动触发类事件冷却时间（毫秒）
const EVENT_COOLDOWN_MS: Partial<Record<ReportEvent, number>> = {
  [ReportEvent.LEARN_GUIDE_RECT]: 10_000,
  [ReportEvent.NOTIFICATION_PERMISSION_STATUS]: 30_000,
  [ReportEvent.NOTIFICATION_PERMISSION_MODAL_SHOWN]: 10_000,
  [ReportEvent.PATH_NAME_CHANGE]: 1_000,
  [ReportEvent.PUSH_NOTIFICATION_CLICKED]: 10_000,
};

const reportInternal = (event: ReportEvent, data: any) => {
  try {
    // 获取用户信息
    const user = useAuthStore.getState().user;

    const buildInfo = {
      version: process.env.NEXT_PUBLIC_BUILD_VERSION || 'dev',
      time: process.env.NEXT_PUBLIC_BUILD_TIME || 'unknown',
      branch: process.env.NEXT_PUBLIC_BUILD_BRANCH || 'local',
      currentTimeUTC: new Date().toISOString(),
      currentTimeLocal: new Date().toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })
    };

    if (isLearnTest) {
      // console.log('%c🔧 Report to PostHog🔧', 'color: #4CAF50; font-weight: bold; font-size: 14px');
      // console.log('event name: ', event);
      // console.log('event data: ', {
      //   ...data,
      //   user_id: user?.id, // 确保传递正确的用户ID
      //   user_email: user?.email,
      //   report_time: new Date().toISOString(),
      //   ...buildInfo,
      // });
      // console.log('%c================================', 'color: #4CAF50');
    } else {
      // 使用 posthog 上报日志
      posthog.capture(event, {
        ...data,
        user_id: user?.id, // 确保传递正确的用户ID
        user_email: user?.email,
        report_time: new Date().toISOString(),
        ...buildInfo,
      });
    }
  } catch (error) {
    // 错误处理
    console.error("Error while reporting event to PostHog:", error);
  }
};

const report = (event: ReportEvent, data: any) => {
  const now = Date.now();
  const cooldownMs = EVENT_COOLDOWN_MS[event];
  const lastTriggerAt = lastEventTriggerAt.get(event) || 0;

  // 自动事件冷却：在窗口期内直接丢弃，避免高频上报触发限流
  if (cooldownMs && now - lastTriggerAt < cooldownMs) {
    return;
  }
  if (cooldownMs) {
    lastEventTriggerAt.set(event, now);
  }

  const normalizedData = normalizeDebounceData(data);

  // 为每个事件类型创建唯一的key
  const debounceKey = `${event}_${JSON.stringify(normalizedData)}`;
  
  // 清除之前的定时器
  const existingTimer = debounceTimers.get(debounceKey);
  if (existingTimer) {
    clearTimeout(existingTimer);
  }
  
  // 设置新的防抖定时器
  const timer = setTimeout(() => {
    reportInternal(event, data);
    debounceTimers.delete(debounceKey);
  }, DEBOUNCE_DELAY);
  
  debounceTimers.set(debounceKey, timer);
};

export default report;
